import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Crown, Trophy, Users as UsersIcon, Mail, Check, X as XIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { listTeams, createTeam, myInvites, acceptInvite, declineInvite, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";

function CreateTeamDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", bio: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const team = await createTeam(form);
      toast.success(`${team.name} forged.`);
      onCreated(team);
      setOpen(false);
      setForm({ name: "", tag: "", bio: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-team-btn"
          className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
          <Plus size={14} className="mr-2" /> Create Team
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#D4AF37]/30 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Create Team</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Team Name</Label>
            <Input required data-testid="team-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Tag (max 8)</Label>
            <Input maxLength={8} data-testid="team-tag-input" value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value.toUpperCase() })}
              placeholder="TGL"
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Bio</Label>
            <Textarea rows={3} data-testid="team-bio-input" value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="team-create-submit"
              className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
              {saving ? "Forging..." : "Forge Team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listTeams().then(setTeams).catch(() => setTeams([])).finally(() => setLoading(false));
    if (user) myInvites().then(setInvites).catch(() => setInvites([]));
  };

  useEffect(reload, [user?.user_id]);

  const handleAccept = async (id) => {
    try { await acceptInvite(id); toast.success("Joined team."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDecline = async (id) => {
    try { await declineInvite(id); toast.success("Invite declined."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-20 px-6">
      <Navbar />
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">⌁ The Houses</p>
            <h1 className="mt-1 font-display text-3xl lg:text-5xl font-black uppercase text-white">
              All <span className="gold-text">Teams</span>
            </h1>
          </div>
          {user && !user.team_id && <CreateTeamDialog onCreated={reload} />}
          {user?.team_id && (
            <Link to={`/teams/${user.team_id}`}
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners">
              <UsersIcon size={14} /> My Team
            </Link>
          )}
        </div>

        {invites.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Pending Invites</h2>
            <div className="space-y-3">
              {invites.map((inv) => (
                <div key={inv.id} data-testid={`invite-${inv.id}`}
                  className="tgl-card cut-corners p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display uppercase">{inv.team_name}</div>
                    <p className="text-xs text-zinc-400">{inv.message || "You've been invited to join this squad."}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handleAccept(inv.id)} data-testid={`accept-invite-${inv.id}`}
                      className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase text-xs cut-corners">
                      <Check size={12} className="mr-1" /> Accept
                    </Button>
                    <Button onClick={() => handleDecline(inv.id)} variant="outline"
                      data-testid={`decline-invite-${inv.id}`}
                      className="border-white/15 text-zinc-300 hover:border-red-500/40 hover:text-red-300">
                      <XIcon size={12} className="mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <p className="text-zinc-500">Loading teams...</p>}
        {!loading && teams.length === 0 && (
          <div className="tgl-card cut-corners p-10 text-center">
            <p className="text-zinc-400">No teams yet. Be the first to forge a squad.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {teams.map((t) => (
            <Link to={`/teams/${t.id}`} key={t.id} data-testid={`team-card-${t.id}`}
              className="tgl-card cut-corners p-6 flex flex-col gap-3 group">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">[{t.tag}]</div>
                  <div className="font-display text-xl font-black uppercase text-white group-hover:text-[#FFE9A0]">{t.name}</div>
                </div>
                <UsersIcon size={20} className="text-[#D4AF37]" />
              </div>
              {t.bio && <p className="text-sm text-zinc-400 line-clamp-2">{t.bio}</p>}
              <div className="flex items-center justify-between text-xs text-zinc-400 mt-2 border-t border-white/5 pt-3">
                <span className="flex items-center gap-1"><Trophy size={12} className="text-[#D4AF37]" /> {t.stats?.wins || 0} wins</span>
                <span>{t.members?.length || 0} {t.members?.length === 1 ? "member" : "members"}</span>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-zinc-500 uppercase tracking-widest">
                <Crown size={10} className="text-[#D4AF37]" />
                {t.members?.find((m) => m.is_captain)?.gamertag || "—"}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
